import { useState, useEffect } from 'react';
import { PenLine, Feather, Sparkles, ArrowRight, Moon, Sun } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { ArticleEditor } from '@/components/ArticleEditor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';

export default function Index() {
  const { user, metadata } = useCurrentUser();
  const { theme, setTheme } = useTheme();
  const [showEditor, setShowEditor] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  // Check for saved draft
  useEffect(() => {
    const draft = localStorage.getItem('inkwell-draft');
    if (draft) {
      setHasDraft(true);
    }
  }, []);

  const loadDraft = () => {
    const draft = localStorage.getItem('inkwell-draft');
    if (draft) {
      return JSON.parse(draft);
    }
    return undefined;
  };

  if (showEditor) {
    return (
      <ArticleEditor
        initialData={hasDraft ? loadDraft() : undefined}
        onBack={() => setShowEditor(false)}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary">
            <PenLine className="w-6 h-6" />
            <span className="font-serif text-xl font-bold">Inkwell</span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="text-muted-foreground"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>
            <LoginArea />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col">
        <section className="relative isolate flex-1 flex items-center justify-center py-16 px-4 overflow-hidden min-h-[70vh]">
          {/* Background Image */}
          <div className="absolute inset-0 -z-10">
            <img
              src="/hero-image.jpg"
              alt=""
              className="w-full h-full object-cover opacity-50 dark:opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/40 to-background" />
          </div>

          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            {/* Decorative Element */}
            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-primary/5 animate-pulse-subtle" />
              </div>
              <div className="relative flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center animate-ink-drop">
                  <Feather className="w-10 h-10 text-primary" />
                </div>
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold mb-6 tracking-tight">
              Write beautifully.
              <br />
              <span className="text-primary">Publish freely.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl mx-auto leading-relaxed font-serif">
              A serene writing experience for publishing long-form articles to Nostr.
              No distractions. No gatekeepers. Just your words.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              {user ? (
                <Button
                  size="lg"
                  onClick={() => setShowEditor(true)}
                  className="gap-2 text-lg px-8 py-6 rounded-full shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all"
                >
                  <PenLine className="w-5 h-5" />
                  Start Writing
                  <ArrowRight className="w-5 h-5" />
                </Button>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <p className="text-muted-foreground text-sm">
                    Sign in with Nostr to start writing
                  </p>
                  <LoginArea className="scale-110" />
                </div>
              )}

              {hasDraft && user && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowEditor(true)}
                  className="gap-2 rounded-full"
                >
                  <Sparkles className="w-5 h-5" />
                  Continue Draft
                </Button>
              )}
            </div>

            {/* User Greeting */}
            {user && metadata?.name && (
              <p className="text-muted-foreground animate-fade-in">
                Welcome back, <span className="text-foreground font-medium">{metadata.name}</span>
              </p>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4 border-t border-border bg-card/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <FeatureCard
                icon={<PenLine className="w-6 h-6" />}
                title="Distraction-free"
                description="A clean, elegant writing environment designed to help you focus on what matters most—your words."
              />
              <FeatureCard
                icon={<Sparkles className="w-6 h-6" />}
                title="Rich Markdown"
                description="Full Markdown support with live preview. Add images, code blocks, quotes, and more with ease."
              />
              <FeatureCard
                icon={<Feather className="w-6 h-6" />}
                title="Truly Yours"
                description="Publish directly to Nostr. Your content stays under your control, forever accessible across clients."
              />
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-serif font-bold text-center mb-12">
              From thought to publication in minutes
            </h2>

            <div className="space-y-8">
              <StepItem
                number={1}
                title="Write your article"
                description="Use our elegant Markdown editor with live preview. Add images, format text, and structure your ideas."
              />
              <StepItem
                number={2}
                title="Add metadata"
                description="Set a title, summary, tags, and optional header image to help readers discover your content."
              />
              <StepItem
                number={3}
                title="Publish to Nostr"
                description="With one click, your article becomes a kind 30023 event, readable across any Nostr client that supports long-form content."
              />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-primary" />
            <span className="font-serif">Inkwell</span>
          </div>
          <p>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
          </p>
          <p>Powered by Nostr · NIP-23</p>
        </div>
      </footer>
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <div className="group p-6 rounded-2xl bg-background border border-border hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
      <div className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center mb-4",
        "bg-primary/10 text-primary",
        "group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
      )}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </div>
  );
}

interface StepItemProps {
  number: number;
  title: string;
  description: string;
}

function StepItem({ number, title, description }: StepItemProps) {
  return (
    <div className="flex gap-6">
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
          {number}
        </div>
      </div>
      <div className="pt-1">
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
